<template>
  <div v-if="!showTemplates" class="import-resume">
    <div class="header">
      <div class="title">{{ $t('popUp.createResume.importResume') }}</div>
    </div>
    <span>{{ $t('popUp.createResume.importDescription') }} :</span>
    <div
      class="file-input"
      :class="[errorClass, { hasFile: files.length }]"
      @dragover.prevent
      @drop.prevent="handleDropFile"
    >
      <div v-if="isLoading" class="loader-container">
        <div class="background"></div>
        <span class="loader"></span>
      </div>
      <template v-if="!files.length">
        <div class="import-resume-input-container">
          <label class="input-label button-create">
            <DownloadIcon /><span>{{ $t('popUp.createResume.importYourFiles') }}</span>
            <input type="file" accept=".pdf" multiple @change="addFile" />
          </label>
          <div class="import-resume-texts">
            <p>{{ $t('popUp.createResume.dropFiles') }}</p>
            <p>{{ $t('popUp.createResume.acceptedFormat') }}</p>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="import-resume-file-list">
          <TransitionGroup name="files">
            <div
              v-for="file in files"
              :key="file"
              class="file-element"
              :class="{ errorFile: calculSize(file.size) > 5 }"
            >
              <FilesIcon />
              <span class="file-name">{{ file.name }}</span>
              <span>&#183;</span>
              <span>{{ calculSize(file.size) }} Mo</span>
              <RemoveIcon class="remove-icon" @click="removeFile(file.id)" />
            </div>
          </TransitionGroup>
        </div>
      </template>
    </div>
    <div class="file-list-infos">
      <span v-if="someFileAreTooBig"><WarningIcon /> {{ $t('popUp.createResume.fileTooLarge') }}</span>
      <span v-if="nbOfFiles > 10"><WarningIcon /> {{ $t('popUp.createResume.tooManyFiles') }}</span>
      <span v-if="nbOfFiles">{{
        `${$t('popUp.createResume.importedFiles', { count: nbOfFiles })} · ${sizeOfFiles} Mo`
      }}</span>
    </div>
    <div class="import-resume-buttons">
      <label class="button-cancel" :class="{ hide: !files.length }">
        <input type="file" accept=".pdf" multiple @change="addFile" />
        <span>{{ $t('popUp.createResume.addFiles') }}</span></label
      >
      <button class="button-create" :class="{ disabled: !canMoveOn && !isLoading }" @click="nextStep">
        {{ $t('popUp.createResume.continue') }} <ArrowRightIcon />
      </button>
    </div>
  </div>
  <ImportTemplateSelection
    v-else
    v-model:selected-template-id="selectedTemplateId"
    v-model:into-organization="intoOrganization"
    :sorted-templates="sortedTemplates"
    @submit="submit"
    @back="showTemplates = false"
  />
</template>

<script setup>
import DownloadIcon from '@/assets/icons/download.svg';
import FilesIcon from '@/assets/icons/files.svg';
import RemoveIcon from '@/assets/icons/remove.svg';
import WarningIcon from '@/assets/icons/warning.svg';
import ArrowRightIcon from '@/assets/icons/arrowRight.svg';
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import ImportTemplateSelection from '@/components/popup/CreateResumePopup/ImportTemplateSelection.vue';

const store = useStore();
const popupResolve = computed(() => store.getters.popupResolve);
const user = computed(() => store.getters.user);

const { sortedTemplates = [] } = defineProps({
  sortedTemplates: Array,
});

const files = ref([]);
const batchId = ref();
const isLoading = ref(false);
const showTemplates = ref(false);
const selectedTemplateId = ref('');
const intoOrganization = ref(false);

const filterFiles = files => {
  if (files.some(file => file.type !== 'application/pdf')) {
    store.dispatch('toaster/addWarnFileIsNotPdf');
    files = files.filter(file => file.type === 'application/pdf');
  }
  return files;
};

const addFile = e => {
  isLoading.value = true;
  const files = filterFiles([...e.target.files]);

  if (files.length) {
    processFiles(files);
  } else {
    isLoading.value = false;
  }
};

const handleDropFile = e => {
  isLoading.value = true;
  const files = filterFiles([...e.dataTransfer.files]);

  if (files.length) {
    processFiles(files);
  } else {
    isLoading.value = false;
  }
};

const processFiles = async loadedFiles => {
  if (!batchId.value) batchId.value = await getBatchId();

  if (!batchId.value) {
    isLoading.value = false;
    return store.dispatch('toaster/addErrorResumeUpload');
  }

  return Promise.all(
    loadedFiles.map(async file => {
      const res = await store.dispatch('resumes.addResumeToBatch', { batchId: batchId.value, file });

      if (!res) return store.dispatch('toaster/addErrorResumeUpload');

      file.id = res;
      files.value.push(file);
    })
  ).then(() => {
    isLoading.value = false;
  });
};

const getBatchId = async () => {
  return await store.dispatch('resumes.getBatchId');
};

const nextStep = async () => {
  showTemplates.value = true;
};

const removeFile = async fileId => {
  isLoading.value = true;
  const res = await store.dispatch('resumes.removeResumeFromBatch', { batchId: batchId.value, fileId });
  if (res) {
    files.value = files.value.filter(item => item.id !== fileId);
    isLoading.value = false;
  } else {
    store.dispatch('toaster/addErrorResumeDelete');
  }
  isLoading.value = false;
};

const calculSize = fileSize => {
  return !fileSize ? 0 : (fileSize / (1024 * 1024)).toFixed(2);
};

const submit = async () => {
  const chosenTemplate = sortedTemplates.find(item => item.id === selectedTemplateId.value);
  const templateOptions = {
    id: selectedTemplateId.value,
    variables: chosenTemplate.variables,
  };

  if (
    await store.dispatch('resumes.startResumesParsing', {
      batchId: batchId.value,
      templateOptions,
      organizationId: intoOrganization.value ? user.value.organization.id : undefined,
    })
  ) {
    popupResolve.value();
  }
};

const canMoveOn = computed(
  () => files.value.length && files.value.length <= 10 && !someFileAreTooBig.value && !isLoading.value
);
const nbOfFiles = computed(() => files.value.length);
const sizeOfFiles = computed(() => {
  const totalSize = files.value.reduce((acc, curr) => acc + curr.size, 0);
  return Number(totalSize / (1024 * 1024)).toFixed(2);
});
const someFileAreTooBig = computed(() => {
  return files.value.find(file => file.size >= 5000000);
});
const errorClass = computed(() => {
  return someFileAreTooBig.value || nbOfFiles.value > 10 ? 'error' : '';
});
</script>

<style lang="scss" scoped>
@use '@/styles/colors.scss';

.import-resume {
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
  width: min(477px, 100%);
  .header {
    border-bottom: 1px solid colors.$light-blue-color;
    width: 100%;
    .title {
      padding-bottom: 0.5rem;
      color: colors.$light-blue-color;
      font-size: 16px;
      font-weight: bold;
      text-transform: uppercase;
    }
  }

  > span {
    font-size: 16px;
    line-height: 20px;
  }

  .file-input {
    width: 100%;
    height: 14.3rem;
    background-image: url('@/assets/border.svg');
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    gap: 7px;
    position: relative;
    overflow: hidden;
    background-color: rgba(#91b6c040, 0.1);

    &.hasFile {
      background-color: #d4e8ed;
    }

    .loader-container {
      position: absolute;
      inset: 0 0 0 0;
      display: flex;
      justify-content: center;
      align-items: center;

      .background {
        position: absolute;
        inset: 0 0 0 0;
        background-color: #2c6470;
        opacity: 0.3;
      }
    }

    &.error {
      background-color: #edd4d4;
    }

    .import-resume-input-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      height: 100%;
    }

    .input-label {
      margin-top: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      text-transform: uppercase;
      white-space: nowrap;
      width: auto;
      align-self: center;
      vertical-align: center;
      max-width: 11rem;
      padding: 4px 55px;
      gap: 1rem;

      > input {
        display: none;
      }
    }

    .import-resume-texts {
      text-align: center;
      font-size: 12px;
      line-height: 14px;
      font-family: Lato, sans-serif;
    }

    .import-resume-file-list {
      overflow-x: hidden;
      .file-element {
        position: relative;
        padding: 12px 16px;
        display: flex;
        align-items: center;
        gap: 8px;
        border-bottom: 1px solid #91b6c0;
        overflow: hidden;

        &.errorFile {
          color: red;
        }

        &:hover {
          > span {
            opacity: 0.6;
          }
        }

        > span {
          cursor: default;
          user-select: none;
        }

        span:last-child {
          margin-left: 0.4rem;
        }

        .file-name {
          max-width: 40%;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .remove-icon {
          position: absolute;
          top: 16px;
          right: 16px;
          width: 15px;
          height: 15px;
          cursor: pointer;
          transition: all 0.3s;

          &:hover {
            scale: 1.1;
            rotate: 360deg;
          }

          &:active {
            scale: 0.9;
          }
        }
      }
    }
  }

  .file-list-infos {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: -0.6rem;

    > span {
      font-size: 12px;
      font-weight: 400;
      line-height: 14px;
      font-family: Lato, sans-serif;
      color: red;
    }

    > span:last-child {
      font-size: 12px;
      font-weight: 400;
      line-height: 14px;
      font-family: Lato, sans-serif;
      color: #979797;
    }
  }
  .import-resume-buttons {
    width: 100%;
    display: flex;
    justify-content: space-between;
    gap: 4rem;

    .button-cancel {
      text-transform: uppercase;
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      margin: 0;

      &.hide {
        visibility: hidden;
        pointer-events: none;
      }

      > input {
        display: none;
      }
    }

    .button-create {
      flex: 1;
      display: flex;
      gap: 1rem;
      align-items: center;
      justify-content: center;
    }

    > button {
      text-transform: uppercase;
      cursor: pointer;
      margin: 0;
      &.disabled {
        pointer-events: none;
      }
    }
  }
}

.templates-section {
  padding-top: 27px;
  display: flex;
  flex-direction: column;
  position: relative;
  box-sizing: border-box;
  height: 100%;
  width: 100%;

  .title-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0 0 12px 0;

    .title {
      color: #000000;
      font-size: 18px;
      font-weight: bold;
      margin-bottom: 4px;
    }

    .field {
      width: 100%;
      margin: 6px 0;
    }

    .templates {
      display: flex;
      gap: 22px;
      flex-wrap: wrap;
      justify-content: flex-start;
      height: 100%;
      width: 100%;
      box-sizing: border-box;
      overflow: scroll;
      margin: 0;
      padding: 1px;
      .template {
        box-sizing: border-box;
        width: 230px;
        height: 325px;
        border-radius: 4px;
        margin-bottom: 40px;
        background-position: center;
        background-size: contain;
        background-repeat: no-repeat;
        box-shadow:
          0 2px 8px -2px rgba(0, 0, 0, 0.1),
          0 4px 12px -4px rgba(0, 0, 0, 0.2);
        border: solid 1px white;
        cursor: pointer;
        &:hover {
          border-color: colors.$light-blue-color;
        }
      }
    }
  }
}

.loader {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  position: relative;
  animation: rotate 1s linear infinite;
}
.loader::before,
.loader::after {
  content: '';
  box-sizing: border-box;
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 5px solid #fff;
  animation: prixClipFix 2s linear infinite;
}
.loader::after {
  inset: 8px;
  transform: rotate3d(90, 90, 0, 180deg);
  border-color: #fff;
}

@keyframes rotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes prixClipFix {
  0% {
    clip-path: polygon(50% 50%, 0 0, 0 0, 0 0, 0 0, 0 0);
  }
  50% {
    clip-path: polygon(50% 50%, 0 0, 100% 0, 100% 0, 100% 0, 100% 0);
  }
  75%,
  100% {
    clip-path: polygon(50% 50%, 0 0, 100% 0, 100% 100%, 100% 100%, 100% 100%);
  }
}

.files-enter-active,
.files-leave-active {
  transition: all 0.4s ease;
}
.files-enter-from {
  opacity: 0;
  transform: translateX(-30px);
}
.files-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
</style>
